<template>
	<section v-if="workingArea" class="working-area-modal-container">
		<Modal 
			v-if="!showManualCallsModal"
			:showCancelBtn="false"
			:showConfirmBtn="false"
			:texts="modalTexts" 
			@close="emitCloseModal" 
			class="working-area-modal" 
			width="medium">
			<div class="modern-form">
				<div class="wizard-stepper">
					<div class="wizard-steps-row">
						<div
							v-for="step in totalSteps"
							:key="`working-area-step-${step}`"
							class="wizard-step"
							:class="{ active: currentStep === step, completed: currentStep > step }"
							@click="onStepClick(step)"
						>
							<span class="wizard-step-index">
								<span v-if="currentStep > step">✓</span>
								<span v-else>{{ step }}</span>
							</span>
							<span class="wizard-step-label">{{ getStepTitle(step) }}</span>
						</div>
					</div>
					<div class="wizard-progress">
						<div class="wizard-progress-bar" :style="{ width: progressPercent }"></div>
					</div>
				</div>

				<!-- Step 1: Basic Information -->
				<div class="form-section" v-if="currentStep === 1">
					<h3 class="section-title">{{ $t('basic_information') || 'Basic Information' }}</h3>
					<div class="form-grid">
						<div class="form-field">
							<label class="field-label">{{ $t('area_name') }}</label>
							<input 
								v-model="workingArea.area_name" 
								type="text" 
								class="modern-input"
								:class="{ 'flash-field': errors.includes('area_name') }"
								:placeholder="$t('area_name')"
								@input="clearError('area_name')"
							/>
						</div>
						<div class="form-field">
							<label class="field-label">{{ $t('notifications') || 'Notifications' }}</label>
							<div class="multi-select">
								<MultiSelectDialog
									v-if="openSelectMenus.notifications"
									:data="notificationOptions"
									:initialSelected="selectedNotifications"
									fieldName="name"
									idFieldName="id"
									:searchable="false"
									:showAllOption="false"
									@selected="handleNotificationSelect"
									@close="toggleSelect('notifications')"
								/>
								<div @click="toggleSelect('notifications')" class="multi-select-input" :class="{ 'has-selections': selectedNotifications.length > 0, 'is-open': openSelectMenus.notifications, 'flash-field': errors.includes('notifications') }">
									<div v-if="selectedNotifications.length > 0" class="selected-items">
										<span 
											v-for="item in selectedNotifications" 
											:key="item.id"
											@click.stop="removeNotification(item.id)" 
											class="selected-item"
										>
											<span class="item-name">{{ item.name }}</span>
											<span class="remove-icon">×</span>
										</span>
									</div>
									<span v-else class="placeholder">{{ $t('select') }} {{ $t('notifications') || 'notifications' }}...</span>
									<span class="dropdown-arrow" :class="{ 'is-open': openSelectMenus.notifications }">▼</span>
								</div>
							</div>
						</div>
						<div class="form-field">
							<label class="field-label">{{ $t('features') || 'Features' }}</label>
							<div class="multi-select">
								<MultiSelectDialog
									v-if="openSelectMenus.features"
									:data="featureOptions"
									:initialSelected="selectedFeatures"
									fieldName="name"
									idFieldName="id"
									:searchable="false"
									:showAllOption="false"
									@selected="handleFeatureSelect"
									@close="toggleSelect('features')"
								/>
								<div @click="toggleSelect('features')" class="multi-select-input" :class="{ 'has-selections': selectedFeatures.length > 0, 'is-open': openSelectMenus.features }">
									<div v-if="selectedFeatures.length > 0" class="selected-items">
										<span 
											v-for="item in selectedFeatures" 
											:key="item.id"
											@click.stop="removeFeature(item.id)" 
											class="selected-item"
										>
											<span class="item-name">{{ item.name }}</span>
											<span class="remove-icon">×</span>
										</span>
									</div>
									<span v-else class="placeholder">{{ $t('select') }} {{ $t('features') || 'features' }}...</span>
									<span class="dropdown-arrow" :class="{ 'is-open': openSelectMenus.features }">▼</span>
								</div>
							</div>
						</div>
					</div>
					<div v-if="workingArea.counter?.isActive || workingArea.offline_calls?.isActive || workingArea.cleandex?.isActive" class="feature-options">
						<div class="form-grid">
							<div class="form-field" v-if="workingArea.counter?.isActive">
								<label class="field-label">{{ $t('amount_limit') }}</label>
								<input type="number" v-model="workingArea.counter.amount_limit" class="modern-input" :class="{ 'flash-field': errors.includes('amount_limit') }" @input="clearError('amount_limit')" />
							</div>
							<div class="form-field" v-if="workingArea.offline_calls.isActive">
								<label class="field-label">{{ $t('offline_duration_calls') }}</label>
								<input type="number" v-model="workingArea.offline_calls.amount_limit" class="modern-input" />
							</div>
							<div class="form-field" v-if="workingArea.cleandex.isActive">
								<label class="field-label">{{ $t('cleandex_warning_level') }}</label>
								<div class="multi-select">
									<MultiSelectDialog
										v-if="openSelectMenus.cleandex_warning_level"
										:data="cleandexWarningLevels"
										:initialSelected="selectedCleandexWarningLevel"
										fieldName="label"
										idFieldName="value"
										:searchable="false"
										:showAllOption="false"
										:singleSelect="true"
										@selected="handleCleandexWarningLevelSelect"
										@close="toggleSelect('cleandex_warning_level')"
									/>
									<div @click="toggleSelect('cleandex_warning_level')" class="multi-select-input" :class="{ 'has-selections': selectedCleandexWarningLevel.length > 0, 'is-open': openSelectMenus.cleandex_warning_level, 'flash-field': errors.includes('cleandex_warning_level') }">
										<div v-if="selectedCleandexWarningLevel.length > 0" class="selected-items">
											<span 
												v-for="item in selectedCleandexWarningLevel" 
												:key="item.value"
												@click.stop="removeCleandexWarningLevel()" 
												class="selected-item"
											>
												<span class="item-name">{{ item.label }}</span>
												<span class="remove-icon">×</span>
											</span>
										</div>
										<span v-else class="placeholder">{{ $t('select_warning_level') || 'Select warning level...' }}</span>
										<span class="dropdown-arrow" :class="{ 'is-open': openSelectMenus.cleandex_warning_level }">▼</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="form-field full-width" style="margin-top: 20px;">
						<button type="button" class="modern-button secondary-button" @click="showManualCallsModal = true">
							{{ $t('manual_calls') }}
						</button>
					</div>
				</div>

				<!-- Step 2: Configuration -->
				<div class="form-section" v-if="currentStep === 2">
					<h3 class="section-title">{{ $t('configuration') || 'Configuration' }}</h3>
					<div class="form-grid">
							<div class="form-field" v-for="(field,idx) in multiSelectFields" :key="field + idx">
							<transition name="fade">
								<div v-if="selectOptions[field]">
									<label class="field-label">{{ $t(field) }}</label>
									<div class="multi-select">
										<MultiSelectDialog
											v-if="openSelectMenus[field]"
											:data="selectOptions[field]"
											:initialSelected="workingArea[field]"
											fieldName="name"
											idFieldName="id"
											@selected="handleMultiSelectInput($event, field)"
											@close="toggleSelect(field)"
										/>
										<div @click="toggleSelect(field)" class="multi-select-input" :class="{ 'has-selections': workingArea[field] && workingArea[field].length > 0, 'is-open': openSelectMenus[field], 'flash-field': errors.includes(field) }">
											<div v-if="workingArea[field] && workingArea[field].length > 0" class="selected-items">
												<span 
													v-for="item in getFilteredItems(workingArea[field])" 
													:key="item.id || _getRandomKey(field)"
													@click.stop="removeItemFromSelected('workingArea', item.id, field)" 
													class="selected-item"
												>
													<span class="item-name">{{ item.name }}</span>
													<span class="remove-icon">×</span>
												</span>
											</div>
											<span v-else class="placeholder">{{ $t('select') }} {{ $t(field) }}...</span>
											<span class="dropdown-arrow" :class="{ 'is-open': openSelectMenus[field] }">▼</span>
										</div>
									</div>
									<span v-if="selectOptions[field].length === 0" class="no-data-message">{{ $t(`no_data_available`) }}</span>
								</div>
							</transition>
						</div>
					</div>
				</div>

				<!-- Step 3: Contact & Settings -->
				<div class="form-section" v-if="currentStep === 3">
					<h3 class="section-title">{{ $t('contact_settings') || 'Contact & Settings' }}</h3>
					<div class="form-grid">
						<div class="form-field">
							<label class="field-label">{{ $t('emails') }}</label>
							<input v-model="emailListStr" class="modern-input" :placeholder="$t('emails')" />
						</div>
						<div class="form-field">
							<label class="field-label">{{ $t('sms') }}</label>
							<input v-model="phoneListStr" class="modern-input" :placeholder="$t('sms')" />
						</div>
					</div>
				</div>
			</div>
			<template v-slot:footer>
				<div class="wizard-footer">
					<button type="button" class="modern-button secondary-button" @click="emitCloseModal">
						{{ $t('cancel') }}
					</button>
					<div class="wizard-footer-right">
						<button
							v-if="currentStep > 1"
							type="button"
							class="modern-button secondary-button"
							@click="prevStep"
						>
							{{ $t('back') || 'Back' }}
						</button>
						<button
							type="button"
							class="modern-button primary-button"
							@click="onPrimaryClick"
							:disabled="isConfirmDisabled"
						>
							{{ primaryStepLabel }}
						</button>
					</div>
				</div>
			</template>
		</Modal>
		<Modal 
			v-if="showManualCallsModal" 
			@submit="showManualCallsModal = false"
			@close="showManualCallsModal = false"
			:showCloseCorner="false"
			:showCancelBtn="false"
			:texts="{ headerPrimary: $t('call_cleaner'), headerSecondary: $t('manual_calls') }"
			width="small"
		>
			<ManualCallsOptions :callOptionsProp="workingArea.manual_calls" @saveManualCallOptions="saveManualCallOptions" />
		</Modal>
	</section>
</template>

<script>
import Modal from '@/modules/common/components/Modal'
import MultiSelectDialog from '@/modules/common/components/MultiSelectDialog'
import ManualCallsOptions from '@/modules/common/components/ManualCallsOptions'
import roomService from '../../control-panel/services/roomService'
import shiftService from '../../control-panel/services/shiftService'
import workingAreaService from '../services/workingAreaService'
import { emptyWorkingArea } from '../consts/consts'

export default {
	name: 'WorkingAreaModal',
	props: {
		workingAreaProp: { type: Object, required: false },
		workingAreasServiks: {
			type: Array,
			required: true
		}
	},
	data() {
		return {
			workingArea: null,
			selectOptions: {
				rooms: [],
				shifts: [],
				active_notification_task: [],
				supervisors: []
			},
			openSelectMenus: {
				rooms: false,
				shifts: false,
				active_notification_task: false,
				supervisors: false,
				notifications: false,
				features: false,
				cleandex_warning_level: false
			},
			supervisorUsers: null,
			emailListStr: '',
			phoneListStr: '',
			isConfirmDisabled: false,
			showManualCallsModal: false,
			currentStep: 1,
			totalSteps: 3,
			errors: [],
			excludedServikIds: new Set()
		}
	},
	async created() {
		this.workingArea = JSON.parse(JSON.stringify(this.workingAreaProp))
		this.excludedServikIds = new Set()
		this._ensureFeatureObjects()
		this.emailListStr = (this.workingArea.emails?.addresses || []).join(',')
		this.phoneListStr = (this.workingArea.sms?.numbers || []).join(',')
		const rooms = await roomService.getRooms(this.filterSelected)
		this.supervisorUsers = await workingAreaService.getUsers(this.filterSelected.site)
		this.selectOptions.rooms = rooms.map((r) => ({ id: r.id, name: r.display_name }))
		this.selectOptions.supervisors = this.supervisorUsers.map((s) => ({ id: s.id, name: s.firstName + ' ' + s.lastName }))
		const shifts = await shiftService.getShifts(this.filterSelected.site)
		this.selectOptions.shifts = shifts.map((s) => ({ id: s.id, name: s.shift_name }))
		this.selectOptions.active_notification_task = this.workingAreasServiks
		this.selectOptions.active_notification_task.forEach((item) => {
			item.name = item.name.split('_').join(' ')
		})
		document.addEventListener('click', this.handleClickOutside)
	},
	beforeDestroy() {
		document.removeEventListener('click', this.handleClickOutside)
	},
	computed: {
		modalTexts() {
			const site = this.$store.getters.currentSiteInfo
			const isEdit = Boolean(this.workingArea?.id)
			const areaName = (this.workingArea?.area_name || '').trim()
			let headerSecondary = site?.name ? `/ ${site.name}` : ''
			if (areaName) {
				headerSecondary += ` — ${areaName}`
			}
			return {
				headerPrimary: isEdit ? 'edit_area' : 'new_area',
				headerSecondary
			}
		},
		multiSelectFields() {
			const fields = ['shifts', 'active_notification_task']
			let fieldsToShow = ['rooms', 'shifts', 'supervisors', 'active_notification_task']
			for (let i = 0; i < fields.length; i++) {
				fieldsToShow.unshift(`${fields[i]}`)
			}
			return ['rooms', 'shifts', 'supervisors', 'active_notification_task']
		},
		filterSelected() {
			return this.$store.getters.filterSelected
		},
		cleandexWarningLevels() {
			return [
				{ value: 85, label: this.$t('bronze') },
				{ value: 125, label: this.$t('silver') },
				{ value: 150, label: this.$t('gold') }
			]
		},
		selectedCleandexWarningLevel() {
			if (!this.workingArea.cleandex?.warning_level) return []
			const level = this.cleandexWarningLevels.find(l => l.value === this.workingArea.cleandex.warning_level)
			return level ? [level] : []
		},
		notificationOptions() {
			return [
				{ id: 'notified', name: this.$t('notified') },
				{ id: 'sms', name: this.$t('sms') },
				{ id: 'email', name: this.$t('email') },
				{ id: 'api', name: 'API' }
			]
		},
		selectedNotifications() {
			const selected = []
			if (this.workingArea.notified) {
				selected.push({ id: 'notified', name: this.$t('notified') })
			}
			if (this.workingArea.sms?.isActive) {
				selected.push({ id: 'sms', name: this.$t('sms') })
			}
			if (this.workingArea.emails?.isActive) {
				selected.push({ id: 'email', name: this.$t('email') })
			}
			if (this.workingArea.api?.isActive) {
				selected.push({ id: 'api', name: 'API' })
			}
			return selected
		},
		featureOptions() {
			return [
				{ id: 'ai', name: this.$t('ai') },
				{ id: 'counter', name: this.$t('counter') },
				{ id: 'cleandex', name: this.$t('cleandex') },
				{ id: 'offline_calls', name: this.$t('offline_calls') }
			]
		},
		selectedFeatures() {
			const selected = []
			if (this.workingArea.is_suggestion) {
				selected.push({ id: 'ai', name: this.$t('ai') })
			}
			if (this.workingArea.counter?.isActive) {
				selected.push({ id: 'counter', name: this.$t('counter') })
			}
			if (this.workingArea.cleandex?.isActive) {
				selected.push({ id: 'cleandex', name: this.$t('cleandex') })
			}
			if (this.workingArea.offline_calls?.isActive) {
				selected.push({ id: 'offline_calls', name: this.$t('offline_calls') })
			}
			return selected
		},
		primaryStepLabel() {
			if (this.currentStep < this.totalSteps) {
				return this.$t('next') || 'Next'
			}
			return this.$t('OK') || 'Save'
		},
		progressPercent() {
			const ratio = this.currentStep / this.totalSteps
			return `${Math.min(1, Math.max(0, ratio)) * 100}%`
		}
	},
	methods: {
		/** Ensures counter / offline_calls / cleandex exist so Counter stays usable if API omits a branch. */
		_ensureFeatureObjects() {
			const d = emptyWorkingArea
			if (!this.workingArea.counter) {
				this.$set(this.workingArea, 'counter', { ...d.counter })
			}
			if (!this.workingArea.offline_calls) {
				this.$set(this.workingArea, 'offline_calls', { ...d.offline_calls })
			}
			if (!this.workingArea.cleandex) {
				this.$set(this.workingArea, 'cleandex', { ...d.cleandex })
			}
		},
		_getRandomKey(field) {
			return `${field}-${Math.random()}`
		},
		_toServikIdKey(id) {
			return String(id)
		},
		_trackExcludedServikIds(previousValues, nextValues) {
			const previousIds = new Set((previousValues || []).map((item) => this._toServikIdKey(item.id)))
			const nextIds = new Set((nextValues || []).map((item) => this._toServikIdKey(item.id)))
			previousIds.forEach((id) => {
				if (!nextIds.has(id)) this.excludedServikIds.add(id)
			})
			nextIds.forEach((id) => {
				if (!previousIds.has(id)) this.excludedServikIds.delete(id)
			})
		},
		async handleMultiSelectInput(values, field) {
			if (!values) return
			const nextValues = Array.isArray(values) ? values.slice() : values
			if (field === 'active_notification_task') {
				this._trackExcludedServikIds(this.workingArea.active_notification_task, nextValues)
			}
			this.workingArea[field] = nextValues
			if (field === 'rooms') {
				if (this.workingArea.rooms.length === 0) this.workingArea.active_notification_task = []
				this.updateActiveNotificationsSelectOptions()
			}
			// Clear error when selection is made
			this.clearError(field)
		},
		removeItemFromSelected(objName, id, field) {
			const idx = this[objName][field].findIndex((item) => this._toServikIdKey(item.id) === this._toServikIdKey(id))
			if (idx !== -1) this[objName][field].splice(idx, 1)
			if (field === 'active_notification_task') {
				this.excludedServikIds.add(this._toServikIdKey(id))
			}
			if (field === 'rooms') this.updateActiveNotificationsSelectOptions()
		},
		async updateActiveNotificationsSelectOptions() {
			// To update the selection list of active_notification_task based on the selected rooms
			const roomsIds = this.workingArea.rooms.map((r) => r.id)
			const defaultServiks = workingAreaService.defaultSelectedWorkingAreaServiks()
			if (roomsIds.length === 0) {
				this.selectOptions.active_notification_task = [...defaultServiks]
				return
			}
			const roomsActiveNotificationTasks = await workingAreaService.getServiksByRoomsIds(roomsIds)
			this.selectOptions.active_notification_task = [...roomsActiveNotificationTasks, ...defaultServiks]
			const availableIds = new Set(this.selectOptions.active_notification_task.map((item) => this._toServikIdKey(item.id)))
			this.workingArea.active_notification_task = this.workingArea.active_notification_task.filter((item) =>
				availableIds.has(this._toServikIdKey(item.id))
			)
			const selectedIds = new Set(this.workingArea.active_notification_task.map((item) => this._toServikIdKey(item.id)))
			const defaultToSelect = workingAreaService.getDefaultToSelectWorkingAreaServiks()
			defaultToSelect.forEach((item) => {
				const itemId = this._toServikIdKey(item.id)
				const isAvailableForRooms = roomsActiveNotificationTasks.some((task) => this._toServikIdKey(task.id) === itemId)
				if (isAvailableForRooms && !selectedIds.has(itemId) && !this.excludedServikIds.has(itemId)) {
					this.workingArea.active_notification_task.push(item)
					selectedIds.add(itemId)
				}
			})
		},
		toggleSelect(field) {
			for (const key in this.openSelectMenus) {
				if (key === field) continue
				if (this.openSelectMenus[key]) this.openSelectMenus[key] = false
			}
			this.openSelectMenus[field] = !this.openSelectMenus[field]
		},
		handleNotificationSelect(selected) {
			if (!selected) return
			const selectedIds = selected.map(item => item.id)
			
			// Update each notification based on selection
			this.workingArea.notified = selectedIds.includes('notified')
			if (this.workingArea.sms) {
				this.workingArea.sms.isActive = selectedIds.includes('sms')
			}
			if (this.workingArea.emails) {
				this.workingArea.emails.isActive = selectedIds.includes('email')
			}
			if (this.workingArea.api) {
				this.workingArea.api.isActive = selectedIds.includes('api')
			}
			
			// Clear error when selection is made
			this.clearError('notifications')
		},
		removeNotification(id) {
			switch(id) {
				case 'notified':
					this.workingArea.notified = false
					break
				case 'sms':
					if (this.workingArea.sms) this.workingArea.sms.isActive = false
					break
				case 'email':
					if (this.workingArea.emails) this.workingArea.emails.isActive = false
					break
				case 'api':
					if (this.workingArea.api) this.workingArea.api.isActive = false
					break
			}
		},
		handleFeatureSelect(selected) {
			if (!selected) return
			const selectedIds = selected.map(item => item.id)
			
			// Update each feature based on selection
			this.workingArea.is_suggestion = selectedIds.includes('ai')
			if (this.workingArea.counter) {
				this.workingArea.counter.isActive = selectedIds.includes('counter')
			}
			if (this.workingArea.cleandex) {
				this.workingArea.cleandex.isActive = selectedIds.includes('cleandex')
			}
			if (this.workingArea.offline_calls) {
				this.workingArea.offline_calls.isActive = selectedIds.includes('offline_calls')
			}
			
			// Clear error when selection is made
			this.clearError('features')
		},
		removeFeature(id) {
			switch(id) {
				case 'ai':
					this.workingArea.is_suggestion = false
					break
				case 'counter':
					if (this.workingArea.counter) this.workingArea.counter.isActive = false
					break
				case 'cleandex':
					if (this.workingArea.cleandex) {
						this.workingArea.cleandex.isActive = false
						this.workingArea.cleandex.warning_level = null
					}
					break
				case 'offline_calls':
					if (this.workingArea.offline_calls) this.workingArea.offline_calls.isActive = false
					break
			}
		},
		handleCleandexWarningLevelSelect(selected) {
			// Single-select behavior: component handles single-select internally
			if (!selected || selected.length === 0) {
				if (this.workingArea.cleandex) {
					this.$set(this.workingArea.cleandex, 'warning_level', null)
				}
			} else {
				// Component ensures only one item is selected
				if (this.workingArea.cleandex && selected.length > 0) {
					this.$set(this.workingArea.cleandex, 'warning_level', selected[0].value)
				}
			}
			// Close the dialog immediately after selection (single-select behavior)
			this.openSelectMenus.cleandex_warning_level = false
			this.clearError('cleandex_warning_level')
		},
		removeCleandexWarningLevel() {
			if (this.workingArea.cleandex) {
				this.$set(this.workingArea.cleandex, 'warning_level', null)
			}
		},
		getFilteredItems(items) {
			if (!items || !Array.isArray(items)) return []
			return items.filter(item => item.name !== 'All')
		},
		nextStep() {
			if (this.currentStep === 1) {
				if (!this.checkStep1()) {
					// Scroll to first error field
					this.$nextTick(() => {
						const errorField = this.$el.querySelector('.flash-field')
						if (errorField) {
							errorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
							errorField.focus()
						}
					})
					return
				}
			}
			if (this.currentStep === 2) {
				if (!this.checkStep2()) {
					this.$nextTick(() => {
						const errorField = this.$el.querySelector('.flash-field')
						if (errorField) {
							errorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
						}
					})
					return
				}
			}
			if (this.currentStep < this.totalSteps) this.currentStep++
		},
		prevStep() {
			if (this.currentStep > 1) this.currentStep--
		},
		onPrimaryClick() {
			if (this.currentStep < this.totalSteps) {
				this.nextStep()
			} else {
				this.emitWorkingArea()
			}
		},
		onStepClick(step) {
			// Allow navigating back to completed steps, but not skipping ahead
			if (step < this.currentStep) {
				this.currentStep = step
			}
		},
		getStepTitle(step) {
			if (step === 1) return this.$t('basic_information') || 'Basic Information'
			if (step === 2) return this.$t('configuration') || 'Configuration'
			return this.$t('contact_settings') || 'Contact & Settings'
		},
		checkStep1() {
			this.errors = []
			let isValid = true
			
			if (!this.workingArea.area_name || !this.workingArea.area_name.trim()) {
				this.errors.push('area_name')
				isValid = false
			}
			
			if (this.selectedNotifications.length === 0) {
				this.errors.push('notifications')
				isValid = false
			}
			
			return isValid
		},
		checkStep2() {
			this.errors = []
			let isValid = true
			
			if (!this.workingArea.rooms || this.workingArea.rooms.length === 0) {
				this.errors.push('rooms')
				isValid = false
			}
			
			if (!this.workingArea.shifts || this.workingArea.shifts.length === 0) {
				this.errors.push('shifts')
				isValid = false
			}
			
			return isValid
		},
		clearError(field) {
			const index = this.errors.indexOf(field)
			if (index > -1) {
				this.errors.splice(index, 1)
			}
		},
		handleClickOutside(event) {
			const target = event.target
			const modalElement = this.$el
			if (!modalElement) return
			
			// Check if click is outside the modal or outside any open dropdown
			const isClickInsideModal = modalElement.contains(target)
			const isClickInsideDropdown = Array.from(document.querySelectorAll('.dialog')).some(dialog => dialog.contains(target))
			
			if (!isClickInsideModal || (isClickInsideModal && !isClickInsideDropdown)) {
				// Close all open select menus if clicking outside dropdowns
				let shouldClose = true
				// Don't close if clicking on the input itself (to allow opening)
				const multiSelectInputs = modalElement.querySelectorAll('.multi-select-input')
				for (const input of multiSelectInputs) {
					if (input.contains(target)) {
						shouldClose = false
						break
					}
				}
				
				if (shouldClose) {
					for (const key in this.openSelectMenus) {
						this.openSelectMenus[key] = false
					}
				}
			}
		},
		emitCloseModal() {
			this.currentStep = 1
			this.errors = []
			this.$emit('closeModal')
		},
		async emitWorkingArea() {
			this.errors = []
			if (!this.checkForm()) {
				// Scroll to first error field
				this.$nextTick(() => {
					const errorField = this.$el.querySelector('.flash-field')
					if (errorField) {
						errorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
						if (errorField.tagName === 'INPUT' || errorField.tagName === 'SELECT') {
							errorField.focus()
						}
					}
				})
				return
			}
			this.isConfirmDisabled = true
			this.$emit('submit', this.workingArea)
		},
		checkForm(){
			this.errors = []
			let isValid = true
			
			if (!this.workingArea.area_name || !this.workingArea.area_name.trim()) {
				this.errors.push('area_name')
				isValid = false
			}
			
			if (this.selectedNotifications.length === 0) {
				this.errors.push('notifications')
				isValid = false
			}
			
			if (!this.workingArea.rooms || this.workingArea.rooms.length === 0) {
				this.errors.push('rooms')
				isValid = false
			}
			
			if (!this.workingArea.shifts || this.workingArea.shifts.length === 0) {
				this.errors.push('shifts')
				isValid = false
			}
			
			if (this.workingArea.counter?.isActive && !this.workingArea.counter.amount_limit) {
				this.errors.push('amount_limit')
				isValid = false
			}
			
			if (this.workingArea.cleandex?.isActive && !this.workingArea.cleandex.warning_level) {
				this.errors.push('cleandex_warning_level')
				isValid = false
			}
			
			return isValid
		},
		saveManualCallOptions(callOptions) {
			if (!Array.isArray(callOptions)) return
			// CallOptions is an array of objects with num, hour and gapInMinutes properties
			if (callOptions.length > 0) {
				if (callOptions.every((option) => {
					if (!option.num || !option.hour || !option.gapInMinutes) return false
					return true
				})) {
					this.workingArea.manual_calls = callOptions
				}
			} else {
				this.workingArea.manual_calls = []
			}
		}
	},
	watch: {
		'workingArea.counter.isActive'(to, from) {
			// To automatically add call cleaner servik to active_notification_task when counter is active
			const callCleanerServikInfo = { id: 216, name: 'call cleaner' }
			const field = 'active_notification_task'
			const isCallCleanerServikActive = this.workingArea[field].find((item) => this._toServikIdKey(item.id) === this._toServikIdKey(callCleanerServikInfo.id))
			if (!from && to) {
				if (this.excludedServikIds.has(this._toServikIdKey(callCleanerServikInfo.id))) return
				if (this.workingArea[field].length === 0) {
					this.workingArea[field] = [callCleanerServikInfo]
				} else if (this.workingArea[field].length > 0 && !isCallCleanerServikActive) {
					this.workingArea[field].push(callCleanerServikInfo)
				}
			} else if (from && !to) {
				if (this.workingArea[field].length === 1) {
					this.workingArea[field] = []
				} else if (this.workingArea[field].length > 1 && isCallCleanerServikActive) {
					this.workingArea[field] = this.workingArea[field].filter((item) => this._toServikIdKey(item.id) !== this._toServikIdKey(callCleanerServikInfo.id))
				}
			}
		},
		'workingArea.cleandex.isActive'(to, from) {
			const cleandexServikInfo = { id: 1053, name: 'cleandex call cleaner' }
			const field = 'active_notification_task'
			const isCleandexServikActive = this.workingArea[field].find((item) => this._toServikIdKey(item.id) === this._toServikIdKey(cleandexServikInfo.id))
			if (!from && to) {
				if (this.excludedServikIds.has(this._toServikIdKey(cleandexServikInfo.id))) return
				if (this.workingArea[field].length === 0) {
					this.workingArea[field] = [cleandexServikInfo]
				} else if (this.workingArea[field].length > 0 && !isCleandexServikActive) {
					this.workingArea[field].push(cleandexServikInfo)
				}
			} else if (from && !to) {
				if (this.workingArea[field].length === 1) {
					this.workingArea[field] = []
				} else if (this.workingArea[field].length > 1 && isCleandexServikActive) {
					this.workingArea[field] = this.workingArea[field].filter((item) => this._toServikIdKey(item.id) !== this._toServikIdKey(cleandexServikInfo.id))
				}
				// Clear warning level when cleandex is disabled
				this.workingArea.cleandex.warning_level = null
			}
		},
		emailListStr(val) {
			this.workingArea.emails.addresses = val.split(',')
		},
		phoneListStr(val) {
			this.workingArea.sms.numbers = val.split('/')
		}
	},
	components: {
		Modal,
		MultiSelectDialog,
		ManualCallsOptions
	}
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

// Modern Color Palette (matching site modal)
$primary: #007bff;
$primary-hover: #0056b3;
$background: #ffffff;
$surface: #f8f9fa;
$border: #e0e0e0;
$border-light: #f0f0f0;
$text-primary: #212529;
$text-secondary: #6c757d;
$shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.05);
$shadow-md: 0 4px 6px rgba(0, 0, 0, 0.07);
$shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.1);
$radius: 8px;
$radius-sm: 6px;

.modern-form {
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 0 24px 24px;
}

.wizard-stepper {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 8px;
	margin-bottom: 24px;
}

.wizard-steps-row {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 16px;
}

.wizard-step {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
	margin-inline-end: 16px;
}

.wizard-step.active {
	.wizard-step-index {
		border-color: $primary;
		background-color: rgba(0, 123, 255, 0.08);
		color: $primary;
	}

	.wizard-step-label {
		color: $primary;
		font-weight: 600;
	}
}

.wizard-step.completed {
	.wizard-step-index {
		border-color: $primary;
		background-color: $primary;
		color: #ffffff;
	}
}

.wizard-step-index {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	border: 2px solid $border;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	color: $text-secondary;
	background-color: $background;
	box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
}

.wizard-step-label {
	font-size: 14px;
	color: $text-secondary;
	font-weight: 500;
}

.wizard-step:hover .wizard-step-index {
	border-color: $primary;
	transform: translateY(-1px);
}

.wizard-progress {
	position: relative;
	width: 100%;
	height: 3px;
	border-radius: 999px;
	background-color: $border-light;
	overflow: hidden;
}

.wizard-progress-bar {
	position: absolute;
	top: 0;
	left: 0;
	height: 100%;
	border-radius: inherit;
	background: linear-gradient(90deg, $primary, lighten($primary, 10%));
	transition: width 0.25s ease;
}

.wizard-footer {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

.wizard-footer-right {
	display: flex;
	gap: 8px;
}

.form-section {
	background: $background;
	border-radius: $radius;
	padding: 20px;
	box-shadow: $shadow-sm;
	border: 1px solid $border-light;
}

.section-title {
	font-size: 16px;
	font-weight: 600;
	color: $text-primary;
	margin: 0 0 20px 0;
	padding-bottom: 12px;
	border-bottom: 2px solid $border-light;
	letter-spacing: -0.01em;
}

.form-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
	gap: 20px;
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: 8px;

	&.full-width {
		grid-column: 1 / -1;
	}
}

.field-label {
	font-size: 13px;
	font-weight: 500;
	color: $text-primary;
	margin: 0;
	letter-spacing: 0.01em;
}

.modern-input {
	width: 100%;
	padding: 10px 14px;
	font-size: 14px;
	border: 1.5px solid $border;
	border-radius: $radius-sm;
	background: $background;
	color: $text-primary;
	transition: all 0.2s ease;

	&:focus {
		outline: none;
		border-color: $primary;
		box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
	}

	&::placeholder {
		color: $text-secondary;
	}

	&:disabled {
		background: $surface;
		cursor: not-allowed;
	}
	
	&.flash-field {
		border-color: #dc3545 !important;
		animation: flashBorder 1s ease-in-out 1;
		background-color: rgba(220, 53, 69, 0.08);
	}
}

.modern-select {
	width: 100% !important;
	height: auto !important;
	min-height: 42px;

	::v-deep .md-input {
		padding: 10px 14px !important;
		font-size: 14px !important;
		border: 1.5px solid $border !important;
		border-radius: $radius-sm !important;
		background-color: $background !important;
		transition: all 0.2s ease !important;
	}

	&:focus-within ::v-deep .md-input {
		border-color: $primary !important;
		box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1) !important;
	}

	&:hover ::v-deep .md-input {
		border-color: darken($border, 8%) !important;
	}
	
	&.flash-field {
		::v-deep .md-input {
			border-color: #dc3545 !important;
			background-color: rgba(220, 53, 69, 0.08) !important;
			animation: flashBorder 1s ease-in-out 1;
		}
	}
}

@keyframes flashBorder {
	0% {
		border-color: #dc3545;
	}
	25% {
		border-color: transparent;
	}
	50% {
		border-color: #dc3545;
	}
	75% {
		border-color: transparent;
	}
	100% {
		border-color: #dc3545;
	}
}

.modern-button {
	padding: 10px 20px;
	font-size: 14px;
	font-weight: 500;
	border: none;
	border-radius: $radius-sm;
	cursor: pointer;
	transition: all 0.2s ease;
	letter-spacing: 0.01em;
}

.modern-button.primary-button {
	background: $primary;
	color: #ffffff;
	box-shadow: $shadow-sm;

	&:hover:not(:disabled) {
		background: $primary-hover;
		box-shadow: $shadow-md;
		transform: translateY(-1px);
	}

	&:active:not(:disabled) {
		transform: translateY(0);
		box-shadow: $shadow-sm;
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
		box-shadow: none;
	}
}

.modern-button.secondary-button {
	background: $surface;
	color: $text-primary;
	border: 1px solid $border;

	&:hover:not(:disabled) {
		background: lighten($surface, 3%);
		border-color: darken($border, 5%);
	}

	&:active:not(:disabled) {
		transform: translateY(0);
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
}

.feature-options {
	margin-top: 20px;
	padding-top: 20px;
	border-top: 1px solid $border-light;
}


.multi-select {
	position: relative;
	width: 100%;
	
	.dialog {
		position: absolute;
		top: calc(100% + 4px);
		left: 0;
		right: 0;
		width: 100%;
		min-width: 100%;
		max-width: 100%;
		z-index: 1000;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
		@include rtl {
			left: 0;
			right: 0;
		}
	}
	
	.no-data-message {
		color: #999;
		font-size: 12px;
		font-style: italic;
		margin-top: 6px;
		padding-left: 2px;
	}
}

.multi-select-input {
	min-height: 42px;
	max-height: 120px;
	border-radius: $radius-sm;
	overflow-y: auto;
	overflow-x: hidden;
	width: 100%;
	cursor: pointer;
	position: relative;
	padding: 6px 30px 6px 10px;
	border: 1.5px solid $border;
	background-color: $background;
	transition: all 0.2s ease;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 5px;
	@include scroll;
	
	&:hover:not(.flash-field) {
		border-color: $primary;
		box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.08);
	}
	
	&.is-open:not(.flash-field) {
		border-color: $primary;
		box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.12);
	}
	
	&.flash-field {
		border-color: #dc3545 !important;
		animation: flashBorder 1s ease-in-out 1;
		background-color: rgba(220, 53, 69, 0.08);
	}
	
	.placeholder {
		color: $text-secondary;
		font-size: 13px;
		user-select: none;
		flex: 1;
		line-height: 30px;
	}
	
	.selected-items {
		display: flex;
		flex-wrap: wrap;
		gap: 5px;
		flex: 1;
		align-items: center;
		min-height: 30px;
	}
	
	.selected-item {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		padding: 3px 8px 3px 10px;
		background-color: #e3f2fd;
		border: 1px solid #90caf9;
		border-radius: 14px;
		font-size: 12px;
		color: #1976d2;
		cursor: pointer;
		transition: all 0.12s ease;
		max-width: 100%;
		line-height: 1.2;
		
		&:hover {
			background-color: #bbdefb;
			border-color: #64b5f6;
		}
		
		.item-name {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			max-width: 140px;
		}
		
		.remove-icon {
			font-size: 14px;
			line-height: 1;
			font-weight: bold;
			color: #1976d2;
			margin-left: 2px;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 14px;
			height: 14px;
			border-radius: 50%;
			transition: background-color 0.12s ease;
			
			&:hover {
				background-color: #90caf9;
			}
		}
	}
	
	.dropdown-arrow {
		position: absolute;
		right: 10px;
		top: 50%;
		transform: translateY(-50%);
		font-size: 9px;
		color: #666;
		transition: transform 0.2s ease;
		pointer-events: none;
		user-select: none;
		
		&.is-open {
			transform: translateY(-50%) rotate(180deg);
		}
	}
}

// Responsive adjustments
@media (max-width: 1024px) {
	.modern-form {
		padding: 0 12px 16px;
	}

	.form-section {
		padding: 16px;
	}

	.form-grid {
		grid-template-columns: 1fr;
		gap: 16px;
	}
}

@media (max-width: 768px) {
	.modern-form {
		padding: 0 8px 12px;
		gap: 16px;
	}

	.form-section {
		padding: 12px;
	}

	.section-title {
		font-size: 14px;
		margin-bottom: 14px;
		padding-bottom: 10px;
	}

	.wizard-stepper {
		margin-bottom: 16px;
	}

	.wizard-steps-row {
		flex-wrap: wrap;
		justify-content: space-between;
		gap: 8px;
	}

	.wizard-step {
		flex: 1 1 30%;
		min-width: 0;
		margin-inline-end: 0;
		flex-direction: column;
		align-items: center;
		gap: 4px;
	}

	.wizard-step-index {
		width: 28px;
		height: 28px;
		font-size: 12px;
	}

	.wizard-step-label {
		font-size: 10px;
		text-align: center;
		line-height: 1.25;
		max-width: 100%;
		word-break: break-word;
	}

	.wizard-footer {
		flex-direction: column;
		align-items: stretch;
		gap: 10px;
	}

	.wizard-footer-right {
		flex-direction: column;
		width: 100%;
		gap: 8px;
	}

	.modern-button {
		width: 100%;
		min-height: 40px;
		padding: 10px 16px;
	}

	.multi-select-input {
		min-height: 44px;
		padding-inline-end: 28px;
	}
}
</style>

<style lang="scss">
.working-area-modal-container {
	.modal {
		width: 80vw !important;
		max-width: 1100px !important;
		display: flex;
		flex-direction: column;
		
		span.close-btn {
			margin-inline-end: 40px !important;
		}

		.footer {
			margin-inline-start: 40px !important;
			margin-inline-end: 50px !important;
			justify-content: flex-end !important;
		}

		.main-content {
			gap: 15px !important;
			margin-bottom: 25px !important;
			margin-top: 25px !important;
		}
	}

	@media (max-width: 768px) {
		.modal {
			width: 96vw !important;
			min-width: 0 !important;
			max-width: 96vw !important;
			max-height: 94vh !important;
			padding-bottom: 24px !important;

			&.small {
				width: 96vw !important;
				min-width: 0 !important;
				max-width: 96vw !important;
			}

			.header {
				min-height: 64px;

				.text-container > span {
					margin-inline-start: 16px !important;
				}

				.primary-title,
				.secondary-title {
					font-size: 18px !important;
					line-height: 22px !important;
				}

				.close-btn {
					margin-inline-end: 12px !important;
				}
			}

			.main-content {
				margin-inline: 16px !important;
				margin-top: 16px !important;
				margin-bottom: 16px !important;
			}

			.footer {
				margin-inline: 16px !important;
				padding-inline-start: 0 !important;
				flex-wrap: wrap;
				gap: 8px;

				.confirm {
					margin-inline-start: 12px !important;
				}
			}
		}
	}
}
</style>
